import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Auth
export const authAPI = {
  register: data => api.post('/auth/register', data),
  login: data => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

// Schools
export const schoolsAPI = {
  getAll: (search = '') => api.get(`/schools?search=${search}`),
  getById: id => api.get(`/schools/${id}`),
  create: data => api.post('/schools', data),
  update: (id, data) => api.put(`/schools/${id}`, data),
  delete: id => api.delete(`/schools/${id}`),
};

// Kits
export const kitsAPI = {
  getAll: (params = {}) => api.get('/kits', { params }),
  getById: id => api.get(`/kits/${id}`),
  getBySchoolGrade: (schoolId, grade) => api.get(`/kits/school/${schoolId}/grade/${grade}`),
  create: data => api.post('/kits', data),
  update: (id, data) => api.put(`/kits/${id}`, data),
  delete: id => api.delete(`/kits/${id}`),
};

// Products
export const productsAPI = {
  getAll: params => api.get('/products', { params }),
  getById: id => api.get(`/products/${id}`),
  getLowStock: () => api.get('/products/low-stock'),
  create: data => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  updateStock: (id, stock_qty) => api.patch(`/products/${id}/stock`, { stock_qty }),
  delete: id => api.delete(`/products/${id}`),
};

// Categories
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  create: data => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: id => api.delete(`/categories/${id}`),

  // Subcategories
  createSub: (categoryId, data) => api.post(`/categories/${categoryId}/subcategories`, data),
  updateSub: (id, data) => api.put(`/categories/subcategories/${id}`, data),
  deleteSub: id => api.delete(`/categories/subcategories/${id}`),
};

// Orders
export const ordersAPI = {
  create: data => api.post('/orders', data),
  getAll: params => api.get('/orders', { params }),
  getById: id => api.get(`/orders/${id}`),
  updateStatus: (id, status) => api.patch(`/orders/${id}/status`, { status }),
  dashboard: () => api.get('/orders/dashboard'),
};

// School List Requests
export const schoolListRequestsAPI = {
  getAll: (params) => api.get('/school-list-requests', { params }),
  getById: id => api.get(`/school-list-requests/${id}`),
  create: data => api.post('/school-list-requests', data),
  updateStatus: (id, data) => api.patch(`/school-list-requests/${id}/status`, data),
  remove: id => api.delete(`/school-list-requests/${id}`),
};

// AI Features
export const aiAPI = {
  uploadList: (formData) => api.post('/ai/upload-list', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

// Tax Rates
export const taxAPI = {
  getAll: () => api.get('/tax-rates'),
  getByState: code => api.get(`/tax-rates/state/${code}`),
  create: data => api.post('/tax-rates', data),
  update: (id, data) => api.put(`/tax-rates/${id}`, data),
  remove: id => api.delete(`/tax-rates/${id}`),
  bulkUpdateShipping: shipping_fee => api.put('/tax-rates/bulk-shipping', { shipping_fee }),
  bulkUpdateTax: tax_rate => api.put('/tax-rates/bulk-tax', { tax_rate }),
};

// Shipping Discounts
export const shippingDiscountAPI = {
  getAll: () => api.get('/shipping-discounts'),
  applyCode: (code, order_amount) => api.post('/shipping-discounts/apply', { code, order_amount }),
  create: data => api.post('/shipping-discounts', data),
  update: (id, data) => api.put(`/shipping-discounts/${id}`, data),
  remove: id => api.delete(`/shipping-discounts/${id}`),
};

// Payments
export const paymentsAPI = {
  // Braintree
  getBraintreeClientToken: () => api.get('/payments/braintree/client-token'),
  processBraintreePayment: data => api.post('/payments/braintree/process', data),

  // Stripe
  createCheckoutSession: order_id => api.post('/payments/checkout-session', { order_id }),

  // History
  getPaymentHistory: order_id => api.get(`/payments/history/${order_id}`),
};

export default api;
